import { useState, useEffect, useCallback, useRef } from 'react';
import { useRegisterOverlay } from '@/stores/overlayStore';
import { useKeyboardAwareOverlay } from '@/hooks/useKeyboardAwareOverlay';
import { Search, Loader2, ChevronLeft } from 'lucide-react';
import {
  Button,
  Input,
  Select,
  EMOJI_COLOR_CLASSES,
  DEFAULT_EMOJIS,
} from '@/components/ui';
import { ChangeProfileImage } from '@/components/domain/shared/ChangeProfileImage';
import { ProfileAvatar } from '@/components/domain/shared/ProfileAvatar';
import { SelectEmoji } from '@/components/domain/shared/SelectEmoji';
import { profileImageService } from '@/services';
import {
  searchSchools,
  SCHOOL_QUERY_MIN_LENGTH,
  type SchoolResult,
} from '@/services/child.service';
import { ONBOARDING_COPY } from './onboardingCopy';

/**
 * Debounce for the school autocomplete input. Places API (New) bills per
 * text search — 500ms is the sweet spot where a typist averages 1-2 requests
 * per school name instead of 4-6.
 */
const SCHOOL_SEARCH_DEBOUNCE_MS = 500;

const GRADE_OPTIONS = [
  { value: 'Pre-K', label: 'Pre-K' },
  { value: 'Kindergarten', label: 'Kindergarten' },
  { value: '1st Grade', label: '1st Grade' },
  { value: '2nd Grade', label: '2nd Grade' },
  { value: '3rd Grade', label: '3rd Grade' },
  { value: '4th Grade', label: '4th Grade' },
  { value: '5th Grade', label: '5th Grade' },
  { value: '6th Grade', label: '6th Grade' },
  { value: '7th Grade', label: '7th Grade' },
  { value: '8th Grade', label: '8th Grade' },
  { value: '9th Grade', label: '9th Grade' },
  { value: '10th Grade', label: '10th Grade' },
  { value: '11th Grade', label: '11th Grade' },
  { value: '12th Grade', label: '12th Grade' },
];

/**
 * Sentence-cases a grade value for the saved-row chip per Figma: only the
 * word "Grade" itself is lowercased ('4th Grade' → '4th grade') — grade
 * levels that don't contain that word pass through unchanged
 * ('Kindergarten' stays 'Kindergarten', 'Pre-K' stays 'Pre-K').
 *
 * Naively lowercasing the whole string and re-uppercasing just the first
 * character looks equivalent at a glance but breaks 'Pre-K' → 'Pre-k'
 * (the internal capital K gets clobbered), so don't "simplify" to that.
 *
 * Co-located with GRADE_OPTIONS, the constant it formats; both are only
 * consumed by this form and StepAddChildren, so the Fast-Refresh scope
 * stays small.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function formatGrade(grade: string): string {
  return grade.replace(/\bGrade\b/g, 'grade');
}

export interface AddEditChildFormProps {
  mode: 'add' | 'edit';
  /**
   * Required in edit mode so avatar uploads can target the existing row.
   * In add mode the child doesn't exist yet — the form stashes the picked
   * File and hands it back in `onSave` so the parent can upload it after
   * creating the child.
   */
  childId?: string;
  initialData?: {
    name: string;
    emoji: string;
    emojiColor: string;
    grade: string;
    school: string;
    imagePath?: string | null;
  };
  onSave: (data: {
    name: string;
    emoji: string;
    emojiColor: string;
    grade: string;
    school: string;
    /** Present only in add mode when the user picked a photo before saving. */
    imageFile?: File | null;
  }) => void;
  onBack: () => void;
}

export function AddEditChildForm({
  mode,
  childId,
  initialData,
  onSave,
  onBack,
}: AddEditChildFormProps) {
  // A full-screen takeover, mounted only while shown: hide and pause the
  // bottom toast slot underneath it (YAR-476).
  useRegisterOverlay(true);
  const copy = ONBOARDING_COPY.children;
  const [name, setName] = useState(initialData?.name ?? '');
  const [emoji, setEmoji] = useState(
    initialData?.emoji ?? (DEFAULT_EMOJIS[0] as string),
  );
  const [emojiColor, setEmojiColor] = useState(
    initialData?.emojiColor ?? (EMOJI_COLOR_CLASSES[0] as string),
  );
  const [imagePath, setImagePath] = useState<string | null>(
    initialData?.imagePath ?? null,
  );
  const [grade, setGrade] = useState(initialData?.grade ?? '');
  const [school, setSchool] = useState(initialData?.school ?? '');
  const [schoolResults, setSchoolResults] = useState<SchoolResult[]>([]);
  const [schoolLoading, setSchoolLoading] = useState(false);
  const [showSchoolDropdown, setShowSchoolDropdown] = useState(false);
  const [showChangeImage, setShowChangeImage] = useState(false);
  const [showSelectEmoji, setShowSelectEmoji] = useState(false);
  // Add mode: stash the picked file locally until the child is created.
  const [pendingImageFile, setPendingImageFile] = useState<File | null>(null);
  const [pendingImagePreview, setPendingImagePreview] = useState<string | null>(
    null,
  );
  const schoolSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const schoolDropdownRef = useRef<HTMLDivElement>(null);
  const [overlayEl, setOverlayEl] = useState<HTMLDivElement | null>(null);
  const keyboardOpen = useKeyboardAwareOverlay(overlayEl);

  // YAR-312 C7: in edit mode Save stays inactive until something differs
  // from the saved child. Add mode keeps the name-only rule.
  const isDirty =
    mode === 'add' ||
    name.trim() !== (initialData?.name ?? '').trim() ||
    emoji !== (initialData?.emoji ?? '') ||
    emojiColor !== (initialData?.emojiColor ?? '') ||
    grade !== (initialData?.grade ?? '') ||
    school.trim() !== (initialData?.school ?? '').trim() ||
    imagePath !== (initialData?.imagePath ?? null);
  const isValid = name.trim().length > 0 && isDirty;

  const handleSave = useCallback(() => {
    if (!isValid) return;
    onSave({
      name: name.trim(),
      emoji,
      emojiColor,
      grade,
      school: school.trim(),
      imageFile: pendingImageFile,
    });
  }, [
    isValid,
    onSave,
    name,
    emoji,
    emojiColor,
    grade,
    school,
    pendingImageFile,
  ]);

  const handleSchoolChange = useCallback((value: string) => {
    setSchool(value);
    if (schoolSearchTimer.current) clearTimeout(schoolSearchTimer.current);
    if (value.trim().length < SCHOOL_QUERY_MIN_LENGTH) {
      setSchoolResults([]);
      setShowSchoolDropdown(false);
      return;
    }
    setSchoolLoading(true);
    setShowSchoolDropdown(true);
    schoolSearchTimer.current = setTimeout(async () => {
      const results = await searchSchools(value.trim());
      setSchoolResults(results);
      setSchoolLoading(false);
    }, SCHOOL_SEARCH_DEBOUNCE_MS);
  }, []);

  const handleSelectSchool = useCallback((result: SchoolResult) => {
    setSchool(result.name);
    setShowSchoolDropdown(false);
    setSchoolResults([]);
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        schoolDropdownRef.current &&
        !schoolDropdownRef.current.contains(e.target as Node)
      ) {
        setShowSchoolDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close dropdown on Escape
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setShowSchoolDropdown(false);
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (schoolSearchTimer.current) clearTimeout(schoolSearchTimer.current);
    };
  }, []);

  const handleUploadImage = useCallback(
    async (file: File) => {
      if (childId) {
        // Edit mode: child exists, upload straight through.
        const { avatar_image_path } =
          await profileImageService.uploadAvatarImage('child', childId, file);
        setImagePath(avatar_image_path);
        setShowChangeImage(false);
        return;
      }
      // Add mode: stash the file locally and show a blob preview until the
      // parent creates the child (then it'll upload with the new id).
      setPendingImageFile(file);
      setPendingImagePreview((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return URL.createObjectURL(file);
      });
      setShowChangeImage(false);
    },
    [childId],
  );

  const handleRemoveImage = useCallback(async () => {
    if (childId) {
      await profileImageService.removeAvatarImage('child', childId);
      setImagePath(null);
      setShowChangeImage(false);
      return;
    }
    // Add mode: discard the pending pick.
    setPendingImageFile(null);
    setPendingImagePreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    setShowChangeImage(false);
  }, [childId]);

  // Revoke blob preview on unmount so we don't leak Object URLs.
  useEffect(() => {
    return () => {
      if (pendingImagePreview) URL.revokeObjectURL(pendingImagePreview);
    };
    // Intentionally track the latest value only on unmount — revoking
    // mid-flight is handled inside the setters above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEmojiSave = useCallback((newEmoji: string, newColor: string) => {
    setEmoji(newEmoji);
    setEmojiColor(newColor);
    setShowSelectEmoji(false);
    setShowChangeImage(false);
  }, []);

  if (showSelectEmoji) {
    return (
      <SelectEmoji
        currentEmoji={emoji}
        currentColor={emojiColor}
        onSave={handleEmojiSave}
        onBack={() => setShowSelectEmoji(false)}
      />
    );
  }

  if (showChangeImage) {
    const hasImage = childId ? !!imagePath : !!pendingImageFile;
    return (
      <ChangeProfileImage
        onBack={() => setShowChangeImage(false)}
        onChooseEmoji={() => {
          setShowChangeImage(false);
          setShowSelectEmoji(true);
        }}
        onUpload={handleUploadImage}
        onRemove={hasImage ? handleRemoveImage : undefined}
        hasImage={hasImage}
        preview={{
          imagePath: childId ? imagePath : null,
          directUrl: pendingImagePreview,
          emoji,
          color: emojiColor,
        }}
      />
    );
  }

  return (
    <div
      ref={setOverlayEl}
      className="ph-no-capture fixed inset-0 z-[200] bg-background overflow-y-auto flex flex-col"
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-edit-child-heading"
    >
      {/* Header carries the back chevron only; the title is the 28px page
          headline below it, matching AddEditCollaboratorForm and every
          other onboarding screen. This form kept the old 16px centred
          header-bar title (YAR-158). */}
      <div className="sticky top-0 z-[100] bg-background">
        <div className="flex items-center px-4 py-3">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center justify-center rounded-md p-1 text-foreground hover:bg-muted transition-colors duration-150 cursor-pointer"
            aria-label="Go back"
          >
            <ChevronLeft size={24} />
          </button>
        </div>
      </div>

      {/* Not a scroll container of its own: the overlay is the ONE scroller,
          so a focused field can be scrolled above the keyboard (YAR-508). A
          nested scroller here never overflowed — the form fits the screen —
          leaving nothing to scroll once the keyboard covered half of it. */}
      <div className="flex-1 px-5 pt-2 pb-6">
        <div className="flex flex-col gap-8">
          <h1
            id="add-edit-child-heading"
            className="text-[28px] leading-8 font-bold text-text-hero"
          >
            {mode === 'add' ? copy.formTitleAdd : copy.formTitleEdit}
          </h1>

          {/* Avatar section */}
          <div className="flex flex-col items-center gap-2">
            <button
              type="button"
              onClick={() => setShowChangeImage(true)}
              className="cursor-pointer rounded-full focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
              aria-label="Tap to change avatar"
            >
              <ProfileAvatar
                imagePath={imagePath}
                directUrl={pendingImagePreview}
                emoji={emoji}
                color={emojiColor}
                size="2xl"
              />
            </button>
            <span className="text-sm text-muted-foreground">
              {copy.avatarCaption}
            </span>
          </div>

          {/* Form fields */}
          <div className="flex flex-col gap-5">
            <Input
              label={copy.nameLabel}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={copy.namePlaceholder}
              autoFocus
            />
            <Select
              label={copy.gradeLabel}
              placeholder={copy.gradePlaceholder}
              value={grade}
              onChange={(e) => setGrade(e.target.value)}
              options={GRADE_OPTIONS}
            />
            {/* Scrolled to the top (clear of the sticky header) rather than
                centred when the keyboard opens, so the results dropdown below
                it has room above the keyboard (YAR-508). */}
            <div
              ref={schoolDropdownRef}
              className="relative scroll-mt-14"
              data-keyboard-scroll="start"
            >
              <Input
                label={copy.schoolLabel}
                value={school}
                onChange={(e) => handleSchoolChange(e.target.value)}
                onFocus={() => {
                  if (
                    schoolResults.length > 0 ||
                    school.trim().length >= SCHOOL_QUERY_MIN_LENGTH
                  )
                    setShowSchoolDropdown(true);
                }}
                placeholder={copy.schoolPlaceholder}
                // Figma puts the 20px glyph at the END of the field, not the
                // start — QA filed both the side and the size (YAR-158).
                rightIcon={<Search size={20} />}
                autoComplete="off"
              />
              {showSchoolDropdown && (
                <div className="absolute left-0 right-0 top-full mt-1 z-[50] bg-white border border-border rounded-sm shadow-lg max-h-64 overflow-y-auto">
                  {schoolLoading ? (
                    <div className="flex items-center justify-center gap-2 px-3 py-4 text-sm text-muted-foreground">
                      <Loader2
                        size={16}
                        className="animate-spin"
                      />
                      <span>{copy.schoolSearching}</span>
                    </div>
                  ) : schoolResults.length > 0 ? (
                    <>
                      {schoolResults.map((result) => (
                        <button
                          key={result.placeId}
                          type="button"
                          className="w-full text-left px-3 py-2.5 hover:bg-muted cursor-pointer transition-colors duration-100"
                          onClick={() => handleSelectSchool(result)}
                        >
                          <span className="block text-sm font-medium text-foreground leading-5">
                            {result.name}
                          </span>
                          <span className="block text-xs text-muted-foreground leading-4 mt-0.5">
                            {result.address}
                          </span>
                        </button>
                      ))}
                      <button
                        type="button"
                        className="w-full text-left px-3 py-2.5 border-t border-border text-sm text-muted-foreground hover:bg-muted cursor-pointer transition-colors duration-100"
                        onClick={() => setShowSchoolDropdown(false)}
                      >
                        {copy.schoolUseCustomName}
                      </button>
                    </>
                  ) : (
                    <div className="px-3 py-4 text-sm text-muted-foreground">
                      {copy.schoolNoResults}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 40px below the CTA = Figma `spacing/component/4xl`, matching
          OnboardingLayout's footer (YAR-158).

          No top border: the frame runs the CTA straight off the content with
          no rule above it, and QA flagged the line as not belonging
          (YAR-158). The footer still sticks and still paints `bg-background`,
          so content scrolling underneath stays covered.

          While the keyboard is open it stops sticking: pinned above the
          keyboard it would take a third of the little space left for the
          field being typed in (YAR-508). It sits at the end of the form
          instead, one scroll away. */}
      <div
        className={[
          'bg-background px-5 pt-4 pb-[max(2.5rem,env(safe-area-inset-bottom))]',
          keyboardOpen ? '' : 'sticky bottom-0',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <Button
          variant="primary"
          fullWidth
          disabled={!isValid}
          onClick={handleSave}
        >
          {mode === 'add' ? copy.formCtaAdd : copy.formCtaEdit}
        </Button>
      </div>
    </div>
  );
}
